import { httpsCallable } from 'firebase/functions'
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check'
import { Capacitor } from '@capacitor/core'
import { getFirebaseApp, getFirebaseFunctions } from './firebase'
import type { AccountAccess, ApprovedBusinessSetup, BusinessAccessResult, ProvisionedBusiness, SubmitBusinessAccessRequest } from '@shared/lib/businessAccess'

// Rollout switches are not authorization. Server gates and rules remain authoritative.
export const onboardingEnabled = import.meta.env.VITE_BUSINESS_ONBOARDING_ENABLED === 'true'
export const signupEnabled = onboardingEnabled && import.meta.env.VITE_BUSINESS_SIGNUP_ENABLED === 'true' && !Capacitor.isNativePlatform()
let appCheckReady = false
function prepareApplicant() {
  if (appCheckReady) return
  const app = getFirebaseApp()
  const siteKey = import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY as string | undefined
  if (!app || !siteKey) throw new Error('Applicant protection unavailable')
  initializeAppCheck(app, { provider: new ReCaptchaEnterpriseProvider(siteKey), isTokenAutoRefreshEnabled: true })
  appCheckReady = true
}
async function call<I, O>(name: string, input: I): Promise<O> {
  const functions = getFirebaseFunctions()
  if (!functions) throw new Error('Service unavailable')
  return (await httpsCallable<I, O>(functions, name)(input)).data
}
export const resolveAccess = () => call<Record<string, never>, AccountAccess>('resolveAccountAccess', {})
export function submitApplication(input: SubmitBusinessAccessRequest) {
  prepareApplicant()
  return call<SubmitBusinessAccessRequest, BusinessAccessResult>('submitBusinessAccessRequest', input)
}
export function provisionBusiness(input: ApprovedBusinessSetup) {
  prepareApplicant()
  return call<ApprovedBusinessSetup, ProvisionedBusiness>('completeApprovedBusinessSetup', input)
}
export function accessError(error: unknown): string {
  const code = (error as { code?: string })?.code
  if (code === 'functions/aborted') return 'Your request changed. Refresh its status before trying again.'
  if (code === 'functions/resource-exhausted' || code === 'auth/too-many-requests') return 'Too many attempts. Please wait before trying again.'
  if (code === 'functions/failed-precondition') return 'This step is not available yet. Refresh your status and try again.'
  return 'We couldn’t complete that step. Check your connection and try again.'
}
